import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    WebhookEndpoint,
    CreateWebhookEndpointRequest,
    UpdateWebhookEndpointRequest,
} from "../../types/webhook";
import { useGetWebhooksQuery } from "../../store/api";

interface WebhookEndpointModalProps {
    isOpen: boolean;
    onClose: () => void;
    endpoint: WebhookEndpoint | null;
    webhookId?: number;
    onSubmit: (
        data: CreateWebhookEndpointRequest | UpdateWebhookEndpointRequest
    ) => void;
    isLoading: boolean;
}

const WebhookEndpointModal: React.FC<WebhookEndpointModalProps> = ({
    isOpen,
    onClose,
    endpoint,
    webhookId,
    onSubmit,
    isLoading,
}) => {
    const { t } = useTranslation("webhooks");

    // Получаем список вебхуков для выбора
    const { data: webhooksData } = useGetWebhooksQuery({
        per_page: 100,
        sort_by: "name",
        sort_order: "asc",
    });

    const [formData, setFormData] = useState<CreateWebhookEndpointRequest>({
        webhook_id: webhookId || 0,
        name: "",
        description: "",
        url: "",
        headers: {},
        payload_mode: "wrapped",
        secret_key: "",
        is_active: true,
        timeout: 10,
        retry_count: 3,
        retry_delay: 60,
        priority: 1,
    });
    const [headersText, setHeadersText] = useState("");

    useEffect(() => {
        if (endpoint) {
            setFormData({
                webhook_id: endpoint.webhook_id,
                name: endpoint.name,
                description: endpoint.description,
                url: endpoint.url,
                headers: endpoint.headers,
                payload_mode: endpoint.payload_mode || "wrapped",
                secret_key: endpoint.secret_key,
                is_active: endpoint.is_active,
                timeout: endpoint.timeout,
                retry_count: endpoint.retry_count,
                retry_delay: endpoint.retry_delay,
                priority: endpoint.priority,
            });
            setHeadersText(
                Object.entries(endpoint.headers)
                    .map(([key, value]) => `${key}: ${value}`)
                    .join("\n")
            );
        } else {
            setFormData({
                webhook_id: webhookId || 0,
                name: "",
                description: "",
                url: "",
                headers: {},
                payload_mode: "wrapped",
                secret_key: "",
                is_active: true,
                timeout: 10,
                retry_count: 3,
                retry_delay: 60,
                priority: 1,
            });
            setHeadersText("");
        }
    }, [endpoint, webhookId]);

    const handleHeadersChange = (text: string) => {
        setHeadersText(text);
        const headers: Record<string, string> = {};
        text.split("\n").forEach((line) => {
            const [key, ...valueParts] = line.split(":");
            if (key && valueParts.length > 0) {
                headers[key.trim()] = valueParts.join(":").trim();
            }
        });
        setFormData({ ...formData, headers });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800">
                <div className="mt-3">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                            {endpoint
                                ? t("endpoints.edit_endpoint")
                                : t("endpoints.create_endpoint")}
                        </h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                        >
                            <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("endpoints.name")}
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            name: e.target.value,
                                        })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("endpoints.priority")}
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    value={formData.priority}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            priority: parseInt(e.target.value),
                                        })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t("endpoints.filters.webhook")}
                            </label>
                            <select
                                value={formData.webhook_id || ""}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        webhook_id: parseInt(e.target.value),
                                    })
                                }
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                                required
                            >
                                <option value="">{t("common:select")}</option>
                                {webhooksData?.data.map((webhook) => (
                                    <option key={webhook.id} value={webhook.id}>
                                        {webhook.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t("endpoints.description")}
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        description: e.target.value,
                                    })
                                }
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t("endpoints.url")}
                            </label>
                            <input
                                type="url"
                                value={formData.url}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        url: e.target.value,
                                    })
                                }
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Формат payload
                            </label>
                            <select
                                value={formData.payload_mode || "wrapped"}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        payload_mode: e.target.value as
                                            | "wrapped"
                                            | "raw",
                                    })
                                }
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                            >
                                <option value="wrapped">
                                    wrapped (как сейчас: event_type/timestamp/data)
                                </option>
                                <option value="raw">raw (как в ТЗ игры)</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t("endpoints.headers")}
                            </label>
                            <textarea
                                value={headersText}
                                onChange={(e) =>
                                    handleHeadersChange(e.target.value)
                                }
                                rows={4}
                                placeholder="Content-Type: application/json&#10;Authorization: Bearer token"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {t("endpoints.headers_help")}
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t("endpoints.secret_key")}
                            </label>
                            <input
                                type="text"
                                value={formData.secret_key}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        secret_key: e.target.value,
                                    })
                                }
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("endpoints.timeout")} (сек)
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="300"
                                    value={formData.timeout}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            timeout: parseInt(e.target.value),
                                        })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("endpoints.retry_count")}
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="10"
                                    value={formData.retry_count}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            retry_count: parseInt(
                                                e.target.value
                                            ),
                                        })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t("endpoints.retry_delay")} (сек)
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="3600"
                                    value={formData.retry_delay}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            retry_delay: parseInt(
                                                e.target.value
                                            ),
                                        })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                                    required
                                />
                            </div>
                        </div>

                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="is_active"
                                checked={formData.is_active}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        is_active: e.target.checked,
                                    })
                                }
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                            />
                            <label
                                htmlFor="is_active"
                                className="ml-2 block text-sm text-gray-900 dark:text-white"
                            >
                                {t("endpoints.is_active")}
                            </label>
                        </div>

                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 rounded-md transition-colors"
                            >
                                {t("common:cancel")}
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50"
                            >
                                {isLoading
                                    ? t("common:loading")
                                    : endpoint
                                    ? t("common:update")
                                    : t("common:create")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default WebhookEndpointModal;
